import React, { Component, Fragment } from 'react'
import { string, object, func, bool, array, number } from 'prop-types'
import InfiniteScroll from 'react-infinite-scroller'
import {
  trackingPageViews,
  urlPathToObject,
  objectToUrlPath,
  getQueryStrings,
  sendGaEventTracking,
  shuffleResults
} from '../../helpers/common'
import {
  EMPTY_ARRAY,
  ALL,
  PROFESSIONALS,
  TYPES_WITH_OFFICES_WIDGET,
  TYPES_WITH_PROFESSIONALS_WIDGET,
  TYPES_WITH_COMPANIES_WIDGET,
  EMPTY_OBJECT,
  IMAGES
} from '../../utils/constants'
import { GalleryContext } from '../../providers/GalleryProvider'
import WidgetContainer from '../search-objects/widgets/WidgetContainer'
import GridWidget from '../search-objects/gridview/GridWidget'
import { PROFESSIONALS_FILTERS_WIDGET } from '../../components/filters/constants'
import { requestNextPage } from '../../utils/requests'
import Loader from '../ui/loader/Loader'

const START_PAGE = 1
const LOADER_KEY = 'loader-key'
const CHUNK_SIZE = 9
const SPONSORED_BANNER_INDEX = 2

const listViewContent = ({ searchType, totalCount, items, renderResultsItem, widgets, widgetsAreLoading, renderWidget }) => {
  let listViewItems = null
  const shouldRenderWidgets = !!widgets.length && !widgetsAreLoading && totalCount > 24
  switch (searchType) {
    case ALL:
      listViewItems = (
        <Fragment>
          {/* first 4 items */}
          {totalCount > 0 && items.length > 0 &&
        items.slice(0, 4).map((item, index) => renderResultsItem(item, index))}
          {/* first widget */}
          { shouldRenderWidgets && renderWidget('first') }
          {/* second items chunk (4 items) */}
          {totalCount > 4 &&
        items.slice(4, 8).map((item, index) => renderResultsItem(item, index + 4))}
          {/* second widget */}
          { shouldRenderWidgets && renderWidget('second') }
          {/* third items chunk (4 items) */}
          {totalCount > 8 &&
        items.slice(8, 12).map((item, index) => renderResultsItem(item, index + 8))}
          {/* third widget */}
          { shouldRenderWidgets && renderWidget('third') }
          {/* fourth chunk (4 items) */}
          {totalCount > 12 &&
        items.slice(12, 16).map((item, index) => renderResultsItem(item, index + 12))}
          {/* fourth widget */}
          { shouldRenderWidgets && renderWidget('fourth') }
          {/* fifth chunk (4 items) */}
          {totalCount > 16 &&
        items
          .slice(16, items.length)
          .map((item, index) => renderResultsItem(item, index + 16))}
        </Fragment>
      )
      break
    case PROFESSIONALS:
      listViewItems = items.map((item, index) => {
        if (index === 4) {
          return (
            <Fragment key={index}>
              <a
                key='professionals-banner'
                className='afd-banner__professionals afd-banner__professionals--search'
                href='https://www.archdaily.com/professionals/us/membership?ad_source=search&ad_medium=banner'
                onClick={() => sendGaEventTracking('professionals-banner-on-search', 'click', 'Professionals Banner on Search')}
              >
                <p className='afd-banner__professionals-text'>
                  Are you on ArchDaily’s list of top Engineers, Consultants and Contractors?</p>
                <div className='afd-banner__professionals-cta'>Join now</div>
              </a>
              {renderResultsItem(item, index)}
            </Fragment>
          )
        } else {
          return renderResultsItem(item, index)
        }
      })
      break
    default:
      listViewItems = items.map((item, index) => renderResultsItem(item, index))
  }
  return listViewItems
}

class MoreResultsContainer extends Component {
  static contextType = GalleryContext;

  constructor (props) {
    super(props)
    this.state = {
      items: props.results || EMPTY_ARRAY,
      productsWidget: !props.widgetsAreLoading && (props.widgets?.['products']?.results || []),
      hasMore: false,
      // eslint-disable-next-line react/no-unused-state
      lastSearchType: props.searchType,
      pageStart: START_PAGE,
      initialLoad: true,
      professionalsWidgetData: EMPTY_ARRAY,
      sponsoredContent: props.sponsoredContent || EMPTY_ARRAY
    }
  }

  componentDidMount () {
    const { location: { search }, results, widgets = EMPTY_OBJECT } = this.props
    const params = new URLSearchParams(search)
    const page = +params.get('page') || ''
    const firstPage = page || START_PAGE
    const widgetProductsResults = widgets['products']?.results
    if (Number.isInteger(page) && page > 1) {
      this.setState({
        pageStart: page,
        productsWidget: widgetProductsResults ? [widgetProductsResults] : [],
        items: results,
        hasMore: results.length === 18
      })
    } else if (results.length === 18) {
      this.setState({
        pageStart: firstPage,
        productsWidget: widgetProductsResults ? [widgetProductsResults] : [],
        items: results,
        hasMore: true
      })
    }
  }

  componentDidUpdate () {
    // Update save button after the component is drawed
    window.MyAD && window.MyAD.renderBookmarkLinks()
  }

  loadItems = page => {
    let professionalsWidgetFilters = ''
    let makeTwoRequests = false
    const { site, scope, query, searchType, history } = this.props
    const oldState = this.state
    const filtersObject = urlPathToObject(location, searchType)
    const filters = objectToUrlPath(filtersObject, searchType)
    if (TYPES_WITH_PROFESSIONALS_WIDGET.includes(searchType) && page === 2) {
      // Run this in the second load (first infinity scroll tirgger)
      const professionalsFiltersObject = urlPathToObject(location, searchType, PROFESSIONALS_FILTERS_WIDGET)
      // Filters allowed to use in Professionals widget. Return a string
      professionalsWidgetFilters = objectToUrlPath(professionalsFiltersObject, searchType)
      // Allow to make two request at the same time (Professionals and Projects request).
      makeTwoRequests = !(!professionalsWidgetFilters && filters)
    } else if (searchType === ALL) {
      makeTwoRequests = true
    }

    const urlParams = { site, scope, query, page, filters, professionalsWidgetFilters, makeTwoRequests }
    const newPath = { search: getQueryStrings(query, page) }
    requestNextPage(
      searchType,
      urlParams,
      newState => {
        if (searchType === IMAGES) {
          // eslint-disable-next-line react/destructuring-assignment
          this.context.addResults(newState.newItems, page)
        }
        this.setState({ ...newState })
        history.push(newPath)
        trackingPageViews()
      },
      oldState
    )
  }

  renderListView = () => {
    const { items } = this.state
    const { renderWidget, renderResultsItem, totalCount, widgets, widgetsAreLoading, searchType } = this.props
    const listParams = { items, renderWidget, renderResultsItem, totalCount, widgets, widgetsAreLoading, searchType }
    return listViewContent(listParams)
  }

  renderGridView = () => {
    const { items, professionalsWidgetData, productsWidget = [], sponsoredContent } = this.state
    const productsWidgetNew = [...productsWidget]
    const { site, scope, renderResultsItem, offices, searchType, query, filters, staticData } = this.props
    const canShowProductsWidget = site === 'us' && searchType === ALL

    const showOfficesWidget = offices.length > 0 && TYPES_WITH_OFFICES_WIDGET.includes(searchType)
    const showCompaniesWidget = filters.companies?.length > 6 && TYPES_WITH_COMPANIES_WIDGET.includes(searchType)

    const chunkedResults = items.reduce((acc, item, index) => {
      const chunkIndex = acc.length ? acc.length - 1 : 0
      const currentChunk = acc[chunkIndex] || []

      if (index === SPONSORED_BANNER_INDEX && sponsoredContent.length > 0) {
        currentChunk.push(
          <Fragment key={`sponsored-${index}`}>
            {renderResultsItem({ ...sponsoredContent[0], document_type: 'sponsored_content' }, index)}
          </Fragment>
        )
      }

      if (currentChunk.length <= CHUNK_SIZE) {
        currentChunk.push(<Fragment key={index}>{renderResultsItem(item, index)}</Fragment>)

        if (canShowProductsWidget && chunkIndex % 2 === 0 && productsWidgetNew.length && currentChunk.length === 7) {
          const productWidgetResults = productsWidgetNew.shift()
          if (productWidgetResults?.length) {
            currentChunk.push(<GridWidget key={`widget-${index}`} type='products'
              query={query} widgetIndex={chunkIndex / 4} results={productWidgetResults} />)
            currentChunk.push(null) // Adding an additional space for the widget
          }
        }
      }
      if (currentChunk.length === CHUNK_SIZE) acc[chunkIndex + 1] = []

      acc[chunkIndex] = currentChunk
      return acc
    }, [])
    // Adding offices widget if available
    if (showOfficesWidget) {
      const officesWidget = <WidgetContainer widgetName='offices' data={offices}
        searchType={searchType} query={query} scope={scope} key='offices-widget' />
      chunkedResults.splice(1, 0, officesWidget)
    }
    // Adding manufacturers widget if available
    if (showCompaniesWidget) {
      const companiesWidget = (
        <WidgetContainer
          widgetName='companies'
          data={filters.companies.slice(0, 24)}
          searchType={searchType}
          query={query}
          viewAllLink={staticData.companies_landing}
          key='companies-widget'
          withoutTitle
        />
      )
      chunkedResults.splice(1, 0, companiesWidget)
    }

    // Adding Professionals widget if available and only in US market and just after the second load.
    if (site === 'us' &&
    professionalsWidgetData && professionalsWidgetData.length > 0 &&
    TYPES_WITH_PROFESSIONALS_WIDGET.includes(searchType)) {
      const chunkPosition = showOfficesWidget ? 3 : 2
      const shuffledWidgetResults = shuffleResults(professionalsWidgetData)
      const professionalsWidget = <WidgetContainer widgetName='professionals' data={shuffledWidgetResults}
        searchType={searchType} query={query} key='professionals-widget' isExpert scope={scope} />
      chunkedResults.splice(chunkPosition, 0, professionalsWidget)
    }

    return chunkedResults
  }

  render () {
    const { pageStart, hasMore, initialLoad } = this.state
    const { gridView } = this.props
    return (
      <InfiniteScroll
        initialLoad={initialLoad}
        pageStart={pageStart}
        loadMore={this.loadItems}
        hasMore={hasMore}
        loader={<Loader key={LOADER_KEY} />}
      >
        {gridView ? this.renderGridView() : this.renderListView()}
      </InfiniteScroll>
    )
  }
}

MoreResultsContainer.propTypes = {
  searchType: string,
  query: string.isRequired,
  history: object,
  location: object,
  site: string,
  scope: string,
  renderResultsItem: func,
  results: array,
  renderWidget: func,
  totalCount: number,
  widgets: object,
  widgetsAreLoading: bool,
  gridView: bool,
  offices: array,
  filters: object,
  staticData: object,
  sponsoredContent: array
}

export default MoreResultsContainer
